import type { StateCreator } from 'zustand';
import type { LaborTree } from '../../types';
import {
  LABOR_BASE_PROB,
  LABOR_DAILY_BASE,
  LABOR_DAILY_MAX,
  LABOR_GOLD_MAX,
  LABOR_GOLD_MIN,
  LABOR_GOLD_PROB,
  LABOR_PROB_STEP,
  LABOR_REWARD_MAX,
  LABOR_REWARD_MIN,
  LABOR_TREE_COUNT,
} from '../../constants';
import { persist } from '../persist';
import { todayStr } from '../../utils/format';
import type { GameState } from '../types';

export interface LaborSlice {
  laborUsed: number;
  laborMax: number;
  laborDate: string;
  laborTrees: LaborTree[];
  laborSessionEarnings: number;

  openLabor: () => void;
  closeLabor: () => void;
  startLaborSession: () => void;
  hitTree: (idx: number) => void;
  watchLaborAd: () => void;
  closeLaborSummary: () => void;
}

export const createLaborSlice: StateCreator<GameState, [], [], LaborSlice> = (set, get) => ({
  laborUsed: 0,
  laborMax: LABOR_DAILY_BASE,
  laborDate: '',
  laborTrees: [],
  laborSessionEarnings: 0,

  openLabor: () => {
    const s = get();
    if (!s.tutDone) { get().showToast('튜토리얼 진행 중이오'); return; }
    const today = todayStr();
    if (s.laborDate !== today) {
      set({ laborDate: today, laborUsed: 0, laborMax: LABOR_DAILY_BASE });
    }
    set({ laborTrees: [], laborSessionEarnings: 0, showLaborScreen: true });
  },

  closeLabor: () => {
    const s = get();
    if ((s.laborSessionEarnings || 0) > 0) {
      set({ showLaborSummaryModal: true });
      return;
    }
    set({ showLaborScreen: false });
  },

  startLaborSession: () => {
    const s = get();
    const today = todayStr();
    if (s.laborDate !== today) {
      set({ laborDate: today, laborUsed: 0, laborMax: LABOR_DAILY_BASE });
    }
    if (get().laborUsed >= get().laborMax) {
      get().showToast('오늘 노역은 끝났소');
      return;
    }
    const sizes: LaborTree['size'][] = ['s-small', 's-medium', 's-large'];
    const trees: LaborTree[] = [];
    for (let i = 0; i < LABOR_TREE_COUNT; i++) {
      trees.push({
        isGold: Math.random() < LABOR_GOLD_PROB,
        prob: LABOR_BASE_PROB,
        fallen: false,
        reward: 0,
        size: sizes[Math.floor(Math.random() * sizes.length)],
      });
    }
    set({ laborTrees: trees, laborUsed: get().laborUsed + 1, laborSessionEarnings: 0 });
    persist(get());
  },

  hitTree: (idx) => {
    const s = get();
    const t = s.laborTrees[idx];
    if (!t || t.fallen) return;
    const fell = Math.random() < t.prob;
    const newTrees = s.laborTrees.map((tr, i) => (i === idx ? { ...tr } : tr));
    const nt = newTrees[idx];
    if (fell) {
      nt.fallen = true;
      if (nt.isGold) {
        nt.reward = Math.floor(LABOR_GOLD_MIN + Math.random() * (LABOR_GOLD_MAX - LABOR_GOLD_MIN));
        get().showToast(`🌟 황금나무! +${Math.round(nt.reward).toLocaleString('ko-KR')}냥`);
      } else {
        nt.reward = Math.floor(LABOR_REWARD_MIN + Math.random() * (LABOR_REWARD_MAX - LABOR_REWARD_MIN));
        get().showToast(`🪵 나무 쓰러짐! +${Math.round(nt.reward).toLocaleString('ko-KR')}냥`);
      }
      set({
        laborTrees: newTrees,
        cash: s.cash + nt.reward,
        laborSessionEarnings: s.laborSessionEarnings + nt.reward,
        stats: { ...s.stats, maxCash: Math.max(s.stats.maxCash, s.cash + nt.reward) },
      });
      if (newTrees.every((tr) => tr.fallen)) {
        setTimeout(() => set({ showLaborSummaryModal: true }), 1100);
      }
    } else {
      nt.prob = Math.min(1, nt.prob + LABOR_PROB_STEP);
      set({ laborTrees: newTrees });
    }
    persist(get());
  },

  watchLaborAd: () => {
    const s = get();
    if (s.laborMax >= LABOR_DAILY_MAX) return;
    const newMax = Math.min(LABOR_DAILY_MAX, s.laborMax + 1);
    set({ laborMax: newMax });
    get().showToast(`노역 +1회 추가! (총 ${newMax}회)`);
    persist(get());
  },

  closeLaborSummary: () => {
    set({
      showLaborSummaryModal: false,
      showLaborScreen: false,
      laborSessionEarnings: 0,
    });
  },
});
